import { Link, useRouterState } from '@tanstack/react-router'
import {
  BookOpen,
  Building2,
  BriefcaseBusiness,
  ChartColumn,
  CreditCard,
  FileText,
  FileUp,
  Inbox,
  LayoutDashboard,
  Package,
  PanelLeft,
  PanelLeftClose,
  Palette,
  Send,
  Settings,
  SquarePen,
  Users,
  Bot,
  Receipt,
  ShoppingCart,
  HandCoins,
  Banknote,
  Landmark,
  Boxes,
  Webhook,
} from 'lucide-react'
import { cx } from '@/lib/clases'
import { useAtajo } from '@/lib/atajos'
import { usePreferencia } from '@/lib/preferencias'
import { BotonIcono } from '@/ui/Boton'
import { useEstadoNomina } from '@/dominio/nomina/useNomina'
import { useEstadoContabilidad } from '@/dominio/contabilidad/useContabilidad'
import { useEstadoCartera } from '@/dominio/cuentas-por-cobrar/useCuentasPorCobrar'

/**
 * Barra lateral de 220px, colapsable a 56px solo con iconos.
 *
 * **Agrupada por frecuencia de uso, no por lógica de sistema.** Es la
 * diferencia entre un menú que se aprende en un día y uno que se consulta
 * siempre.
 *
 * Icono de 20px más texto. Sin iconos decorativos. El elemento activo lleva
 * fondo tenue y una barra de 3px en el borde izquierdo con el color de marca.
 *
 * **La nómina es un complemento** (`docs/adr/0018`): su grupo solo aparece si
 * el contribuyente la tiene encendida y el usuario puede verla. El servidor lo
 * vuelve a exigir en cada ruta; esconder el menú es comodidad, no seguridad.
 * La contabilidad (`docs/adr/0025`) sigue la misma regla.
 *
 * El área de desarrollador no aparece aquí en la v1: está pospuesta. Cuando
 * entre, va como grupo propio al final, no mezclada con Ajustes — su usuario
 * es otro.
 */

interface Elemento {
  a: string
  etiqueta: string
  Icono: typeof FileText
  atajo?: string
}

interface Grupo {
  titulo: string
  elementos: Elemento[]
}

const GRUPOS: readonly Grupo[] = [
  {
    titulo: 'Diario',
    elementos: [
      { a: '/inicio', etiqueta: 'Inicio', Icono: LayoutDashboard },
      { a: '/emitir', etiqueta: 'Emitir', Icono: SquarePen, atajo: 'N' },
      { a: '/documentos', etiqueta: 'Documentos', Icono: FileText },
      { a: '/recibidos', etiqueta: 'Recibidos', Icono: Inbox },
      // Va en «Diario» y no en «Periódico» porque transmitir a DGII y entregarle el
      // comprobante al comprador son dos obligaciones distintas, y la segunda se vigila
      // todos los días. Escondida en otro grupo, un comprobante aceptado por DGII y nunca
      // entregado no lo ve nadie hasta que el cliente reclama.
      { a: '/entregas', etiqueta: 'Entregas', Icono: Send },
    ],
  },
  {
    titulo: 'Periódico',
    elementos: [
      { a: '/reportes', etiqueta: 'Reportes', Icono: ChartColumn },
      // Junto a los reportes porque es lo que los completa: lo emitido o recibido fuera
      // de la plataforma, para que el 606, el 607 y el 608 no salgan cortos.
      { a: '/importacion', etiqueta: 'Importar comprobantes', Icono: FileUp },
    ],
  },
  {
    titulo: 'Mantenimiento',
    elementos: [
      { a: '/clientes', etiqueta: 'Clientes', Icono: Building2 },
      { a: '/productos', etiqueta: 'Productos', Icono: Package },
    ],
  },
  {
    titulo: 'Ajustes',
    elementos: [
      { a: '/configuracion', etiqueta: 'Configuración', Icono: Settings },
      // La cuenta con Agentical Group: plan, cargos y cómo pagar (docs/adr/0020).
      { a: '/suscripcion', etiqueta: 'Mi suscripción', Icono: CreditCard },
      // Qué despacho contable lleva la contabilidad, y quién de él ve qué (docs/adr/0021).
      { a: '/contador', etiqueta: 'Contador', Icono: BriefcaseBusiness },
    ],
  },
  {
    // Grupo propio al final, como pedía la nota de arriba para lo que no es del día a día: las claves con
    // que un asistente de IA entra a la plataforma (docs/adr/0026). Solo las gestiona un administrador;
    // para los demás la pantalla lo dice y el servidor lo exige.
    titulo: 'Administración',
    elementos: [
      { a: '/inteligencia-artificial', etiqueta: 'Inteligencia artificial', Icono: Bot },
      // Los avisos firmados a otro sistema (docs/adr/0030): como las claves de IA, los gestiona un administrador.
      { a: '/webhooks', etiqueta: 'Webhooks', Icono: Webhook },
    ],
  },
]

/**
 * Compras y pagos (docs/adr/0028): del plan base, se usan cada semana. Van detrás de «Diario», delante de
 * «Periódico»: un gasto se registra el día que llega la factura, el 606 se arma una vez al mes.
 */
const GRUPO_COMPRAS: Grupo = {
  titulo: 'Compras',
  elementos: [
    { a: '/gastos', etiqueta: 'Gastos', Icono: Receipt },
    { a: '/compras', etiqueta: 'Órdenes de compra', Icono: ShoppingCart },
    { a: '/cuentas-por-pagar', etiqueta: 'Cuentas por pagar', Icono: HandCoins },
    { a: '/pagos', etiqueta: 'Pagos', Icono: Banknote },
  ],
}

/**
 * En el teléfono (por debajo de `md`) no hay sitio para una columna fija: la barra se esconde y se abre
 * encima del contenido con el botón de la barra superior, siempre ancha, y se cierra al elegir, al tocar
 * fuera o con Escape. En la caja de escritorio y en la app del portal es la misma página.
 */
export function BarraLateral({
  abiertaEnMovil = false,
  alCerrarEnMovil,
}: {
  abiertaEnMovil?: boolean
  alCerrarEnMovil?: () => void
} = {}) {
  const [preferenciaColapsada, setColapsada] = usePreferencia('barraLateral.colapsada', false)
  // Encima del contenido siempre va ancha: colapsada no se lee en un teléfono.
  const colapsada = preferenciaColapsada && !abiertaEnMovil
  const cerrar = alCerrarEnMovil ?? (() => {})
  useAtajo({ tecla: 'Escape', dentroDeCampos: true }, cerrar, abiertaEnMovil)
  const nomina = useEstadoNomina()
  const contabilidad = useEstadoContabilidad()
  const cartera = useEstadoCartera()

  // Los complementos van detrás de «Periódico»: se usan con esa frecuencia, no a diario.
  const complementos: Grupo[] = [
    ...(nomina.data?.contratada ? [{ titulo: 'Personal', elementos: [{ a: '/nomina', etiqueta: 'Nómina', Icono: Users }] }] : []),
    ...(contabilidad.data?.contratada
      ? [
          {
            titulo: 'Contabilidad',
            elementos: [
              { a: '/contabilidad', etiqueta: 'Contabilidad', Icono: BookOpen },
              // Bancos y activos fijos son del mismo complemento (docs/adr/0028).
              { a: '/bancos', etiqueta: 'Bancos', Icono: Landmark },
              { a: '/activos-fijos', etiqueta: 'Activos fijos', Icono: Boxes },
            ],
          },
        ]
      : []),
    // Las cuentas por cobrar son del plan base (docs/adr/0027): aparecen para el rol que las ve, sin complemento.
    ...(cartera.data?.puedeVer
      ? [{ titulo: 'Ventas', elementos: [{ a: '/cuentas-por-cobrar', etiqueta: 'Cuentas por cobrar', Icono: HandCoins }] }]
      : []),
  ]
  const base: readonly Grupo[] = complementos.length > 0 ? [...GRUPOS.slice(0, 2), ...complementos, ...GRUPOS.slice(2)] : GRUPOS
  const posicionPeriodico = base.findIndex((g) => g.titulo === 'Periódico')
  const grupos: readonly Grupo[] =
    posicionPeriodico < 0 ? [...base, GRUPO_COMPRAS] : [...base.slice(0, posicionPeriodico), GRUPO_COMPRAS, ...base.slice(posicionPeriodico)]

  return (
    <>
      {abiertaEnMovil ? (
        <button
          type="button"
          aria-label="Cerrar el menú"
          onClick={cerrar}
          className="fixed inset-0 z-[var(--capa-panel)] bg-gris-900/30 md:hidden"
        />
      ) : null}
      <nav
        aria-label="Navegación principal"
        style={{
          width: colapsada ? 'var(--ancho-barra-lateral-colapsada)' : 'var(--ancho-barra-lateral)',
        }}
        className={cx(
          'shrink-0 flex-col border-r border-gris-300 bg-white sin-impresion',
          abiertaEnMovil ? 'fixed inset-y-0 left-0 z-[var(--capa-panel)] flex shadow-flotante' : 'hidden',
          'md:static md:z-auto md:flex md:shadow-none',
        )}
        onClick={(evento) => {
          // Elegir un destino cierra el menú del teléfono; en el escritorio no hace nada.
          if (abiertaEnMovil && (evento.target as HTMLElement).closest('a')) cerrar()
        }}
      >
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto py-3">
          {grupos.map((grupo) => (
            <section key={grupo.titulo}>
              {!colapsada ? (
                <h2 className="px-3 pb-1 text-etiqueta font-medium uppercase tracking-wide text-gris-600">
                  {grupo.titulo}
                </h2>
              ) : (
                <div aria-hidden="true" className="mx-3 mb-1 border-t border-gris-200" />
              )}
              <ul>
                {grupo.elementos.map((elemento) => (
                  <li key={elemento.a}>
                    <EnlaceMenu elemento={elemento} colapsada={colapsada} />
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <section className="mt-auto">
            {!colapsada ? (
              <h2 className="px-3 pb-1 text-etiqueta font-medium uppercase tracking-wide text-gris-600">
                Referencia
              </h2>
            ) : (
              <div aria-hidden="true" className="mx-3 mb-1 border-t border-gris-200" />
            )}
            <ul>
              <li>
                <EnlaceMenu
                  elemento={{ a: '/sistema', etiqueta: 'Sistema de diseño', Icono: Palette }}
                  colapsada={colapsada}
                />
              </li>
            </ul>
          </section>
        </div>

        <div className="hidden border-t border-gris-300 p-1 md:block">
          <BotonIcono
            etiqueta={colapsada ? 'Expandir la barra lateral' : 'Colapsar la barra lateral'}
            icono={colapsada ? <PanelLeft /> : <PanelLeftClose />}
            onClick={() => setColapsada(!preferenciaColapsada)}
          />
        </div>
      </nav>
    </>
  )
}

function EnlaceMenu({ elemento, colapsada }: { elemento: Elemento; colapsada: boolean }) {
  const { a, etiqueta, Icono, atajo } = elemento

  // Se calcula aquí en vez de usar `activeProps` para poder resolver los
  // conflictos de clases con `cx`: dos utilidades de la misma propiedad
  // concatenadas dejan el resultado a merced del orden de la hoja de estilos.
  const rutaActual = useRouterState({ select: (estado) => estado.location.pathname })
  const activo = rutaActual === a || rutaActual.startsWith(`${a}/`)

  return (
    <Link
      to={a}
      title={colapsada ? etiqueta : undefined}
      aria-current={activo ? 'page' : undefined}
      className={cx(
        'flex h-[var(--alto-control)] items-center gap-3 border-l-[3px] px-3 text-cuerpo',
        // El elemento activo: fondo tenue y barra de 3px con el color de marca.
        activo
          ? 'border-marca bg-marca-tenue font-medium text-marca'
          : 'border-transparent text-gris-700 hover:bg-gris-100',
      )}
    >
      <Icono aria-hidden="true" className="size-5 shrink-0" />
      {!colapsada ? (
        <>
          <span className="flex-1 truncate">{etiqueta}</span>
          {/* Los atajos se muestran en las opciones de menú. */}
          {atajo ? (
            <span aria-hidden="true" className="mono text-[11px] text-gris-600">
              {atajo}
            </span>
          ) : null}
        </>
      ) : null}
    </Link>
  )
}
